import { createContext, useContext, useState, useCallback } from 'react'

export type NotifType = 'trade' | 'alert' | 'system' | 'warning'

export interface Notification {
  id: string
  type: NotifType
  title: string
  message: string
  time: Date
  read: boolean
}

interface NotifContextValue {
  notifications: Notification[]
  unreadCount: number
  addNotification: (title: string, message: string, type?: NotifType) => void
  markRead: (id: string) => void
  markAllRead: () => void
  remove: (id: string) => void
  clearAll: () => void
}

const NotifContext = createContext<NotifContextValue | null>(null)

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const unreadCount = notifications.filter(n => !n.read).length

  const addNotification = useCallback((title: string, message: string, type: NotifType = 'system') => {
    const notif: Notification = {
      id: `notif-${Date.now()}`,
      type, title, message,
      time: new Date(),
      read: false,
    }
    setNotifications(prev => [notif, ...prev].slice(0, 50)) // cap at 50
  }, [])

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }, [])

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  const remove = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const clearAll = useCallback(() => setNotifications([]), [])

  return (
    <NotifContext.Provider value={{ notifications, unreadCount, addNotification, markRead, markAllRead, remove, clearAll }}>
      {children}
    </NotifContext.Provider>
  )
}

export function useNotifications() {
  const ctx = useContext(NotifContext)
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider')
  return ctx
}

// ── Helpers ──
export function timeAgo(date: Date): string {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}
